import type { Economy } from '@audigoal/core';

type AchievementDef = Economy.AchievementDef;

/**
 * Achievements (plan doc 13).
 *
 * Eighteen counters with tiered thresholds. The descriptions are written to be
 * read on a card before the thing has been done — "play a hundred clean chord
 * changes" tells a learner what to go and do, where "chord changes counted"
 * only tells them what the app is watching.
 *
 * Top tiers award a legendary cosmetic, which is the only way to get one: plan
 * doc 13 says legendaries are never purchasable.
 */
export const ACHIEVEMENTS: AchievementDef[] = [
  {
    id: 'ach.wildfire',
    name: 'Wildfire',
    description: 'Keep a streak alive, day after day.',
    metric: 'longestStreak',
    category: 'habit',
    tiers: [3, 7, 14, 30, 50, 100, 200, 365],
    tierRewards: { 2: 'cos.ember-headband', 4: 'cos.flame-crown', 6: 'cos.aura-wildfire' },
    topReward: 'cos.phoenix-skin',
  },
  {
    id: 'ach.sage',
    name: 'Sage',
    description: 'Earn XP, in total, across everything you do.',
    metric: 'totalXp',
    category: 'learning',
    tiers: [100, 500, 1000, 2500, 5000, 10000, 25000, 50000],
    topReward: 'cos.sage-mantle',
  },
  {
    id: 'ach.scholar',
    name: 'Scholar',
    description: 'Master skills until the app stops asking about them.',
    metric: 'skillsMastered',
    category: 'learning',
    tiers: [5, 15, 30, 60, 100, 150, 250],
    topReward: 'cos.circle-cape',
  },
  {
    id: 'ach.sharpshooter',
    name: 'Sharpshooter',
    description: 'Finish lessons without a single wrong answer.',
    metric: 'perfectLessons',
    category: 'learning',
    tiers: [1, 5, 15, 30, 60, 100, 200],
    tierRewards: { 4: 'cos.aura-halo' },
    topReward: 'cos.sharpshooter-badge',
  },
  {
    id: 'ach.virtuoso',
    name: 'Virtuoso',
    description: 'Pass exercises by actually playing them.',
    metric: 'micPasses',
    category: 'playing',
    tiers: [10, 50, 150, 400, 1000, 2500],
    tierRewards: { 4: 'cos.aura-oscilloscope' },
    topReward: 'cos.virtuoso-cloak',
  },
  {
    id: 'ach.songbird',
    name: 'Songbird',
    description: 'Earn three stars on a song.',
    metric: 'songsThreeStars',
    category: 'playing',
    tiers: [1, 3, 7, 15, 25, 40],
    tierRewards: { 3: 'cos.tiny-uke' },
    topReward: 'cos.golden-uke',
  },
  {
    id: 'ach.golden-ear',
    name: 'Golden Ear',
    description: 'Get ear-training questions right.',
    metric: 'earCorrect',
    category: 'learning',
    tiers: [25, 100, 300, 700, 1500, 3000],
    tierRewards: { 5: 'cos.aura-constellation' },
    topReward: 'cos.golden-ear-cuff',
  },
  {
    id: 'ach.conductor',
    name: 'Conductor',
    description: 'Finish whole units of the course.',
    metric: 'unitsCompleted',
    category: 'learning',
    tiers: [1, 5, 10, 20, 35, 55, 75],
    topReward: 'cos.aura-jazz-club',
  },
  {
    id: 'ach.bandmate',
    name: 'Bandmate',
    description: 'Play the set with your ensemble for a week.',
    metric: 'ensembleWeeks',
    category: 'competition',
    tiers: [1, 3, 5, 10, 20],
    topReward: 'cos.medal-gold',
  },
  {
    id: 'ach.legend',
    name: 'Legend',
    description: 'Beat a legendary level, where one mistake ends the run.',
    metric: 'legendaryLessons',
    category: 'learning',
    tiers: [1, 5, 15, 40, 100],
    topReward: 'cos.legend-mantle',
  },
  {
    id: 'ach.strummer',
    name: 'Strummer',
    description: 'Change cleanly between chords, over and over.',
    metric: 'chordChanges',
    category: 'playing',
    tiers: [100, 500, 2000, 5000],
    topReward: 'cos.gold-uke-skin',
  },
  {
    id: 'ach.windrunner',
    name: 'Windrunner',
    description: 'Hold a long, steady tone on the ocarina.',
    metric: 'longTones',
    category: 'playing',
    tiers: [10, 50, 200, 500],
    tierRewards: { 3: 'cos.aura-moon' },
    topReward: 'cos.crystal-ocarina',
  },
  {
    id: 'ach.speedster',
    name: 'Speedster',
    description: 'Score high in a speed round.',
    metric: 'bestSpeedRound',
    category: 'competition',
    tiers: [15, 25, 35, 50],
    topReward: 'cos.speed-laurels',
  },
  {
    id: 'ach.collector',
    name: 'Collector',
    description: 'Collect cosmetics for your companion.',
    metric: 'cosmeticsOwned',
    category: 'collection',
    tiers: [3, 10, 25, 50],
    topReward: 'cos.collector-case',
  },
  {
    id: 'ach.night-owl',
    name: 'Night Owl',
    description: 'Practice after eleven at night.',
    metric: 'lateLessons',
    category: 'habit',
    tiers: [1, 10, 50],
    topReward: 'cos.nightcap-star',
  },
  {
    id: 'ach.early-bird',
    name: 'Early Bird',
    description: 'Practice before seven in the morning.',
    metric: 'earlyLessons',
    category: 'habit',
    tiers: [1, 10, 50],
    topReward: 'cos.sunrise-aura',
  },
  {
    id: 'ach.marathoner',
    name: 'Marathoner',
    description: 'Complete ten lessons in one day.',
    metric: 'marathonDays',
    category: 'habit',
    tiers: [1, 5, 20],
    topReward: 'cos.marathon-ribbon',
  },
  {
    id: 'ach.friendly',
    name: 'Friendly',
    description: 'Follow other learners.',
    metric: 'following',
    category: 'collection',
    tiers: [3, 10, 25],
    topReward: 'cos.friendship-pin',
  },
];

const BY_ID = new Map(ACHIEVEMENTS.map((achievement) => [achievement.id, achievement]));

export function achievementById(id: string): AchievementDef | undefined {
  return BY_ID.get(id);
}

/** Order the achievements screen groups by. */
export const ACHIEVEMENT_CATEGORIES = [
  'habit',
  'learning',
  'playing',
  'competition',
  'collection',
] as const;
